import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { formatPresenceAge, inBoundary, PIN_CATEGORIES, stalenessBucket, type HistorySegment, type PinCategory, type PresenceFix, type PropertyBoundary, type PropertyMembership } from '@cht/shared'
import { MapView, type MapLayers } from './MapView.js'
import { platform } from '../core/platform/index.js'
import { authClient, type SessionUser } from '../core/auth/auth-client.js'
import { appInstaller } from '../core/pwa/install.js'
import { appUpdater } from '../core/pwa/update.js'
import { clearPrivateCaches } from '../core/pwa/private-cache.js'
import { FieldClient } from '../core/api/field-client.js'
import { FieldSession, type FieldSnapshot } from '../core/geo/field-session.js'
import { propertyStore } from '../core/property/property-store.js'
import { db } from '../core/db.js'
import { PinStore, type StoredPin } from '../core/pins/pin-store.js'
import { PresenceClient, type PresenceSnapshot } from '../core/presence/presence-client.js'
import { MediaStore, type StoredMedia } from '../core/capture/media-store.js'
import { MediaCapture } from './MediaCapture.js'
import { MediaLibrary } from './MediaLibrary.js'
import { HistoryPanel } from './HistoryPanel.js'
import { TrailManager } from './TrailManager.js'
import { PeopleAdmin } from './PeopleAdmin.js'
import { StoragePanel } from './StoragePanel.js'
import { QueuePanel } from './QueuePanel.js'
import { Button, ConfirmSheet, ContextRail, InlineNotice, StatusStrip, type RailSection } from './components.js'
import { HardDrive, History, Images, Layers3, Map, MapPin, Navigation, Route, Upload, Users } from 'lucide-react'
import brandMark from '../assets/mark-simple.svg'

const emptyField: FieldSnapshot = { tracking: false, fence: 'checking', fix: undefined, error: undefined, trailMode: 'idle', trailDraft: undefined, trails: [], trailIssue: undefined }
const emptyPresence: PresenceSnapshot = { people: [], connection: 'connecting', snapshotStatus: 'loading' }
type ActiveView = 'map' | 'layers' | 'people' | 'pins' | 'media' | 'trails' | 'history' | 'queue' | 'admin-people' | 'storage'
type UiNotice = { tone: 'ok' | 'fail' | 'info'; title: string; message: string }
const memberRail: RailSection[] = [{ label: 'Field', items: [
  { id: 'map', label: 'Map', icon: Map },
  { id: 'layers', label: 'Layers', icon: Layers3 },
  { id: 'people', label: 'People', icon: Users },
  { id: 'pins', label: 'Pins', icon: MapPin },
  { id: 'media', label: 'Media', icon: Images },
  { id: 'trails', label: 'Trails', icon: Route },
  { id: 'history', label: 'History', icon: History },
  { id: 'queue', label: 'Queue', icon: Upload }
] }]

/** Ask the service worker to pull this property's basemap down for offline use. */
function precacheTiles(propertyId: string): void {
  navigator.serviceWorker?.ready
    .then((registration) => registration.active?.postMessage({ type: 'PRECACHE_TILES', propertyId }))
    .catch(() => undefined)
}

export function App() {
  const [online, setOnline] = useState(platform.network.isOnline())
  const [user, setUser] = useState<SessionUser | undefined>()
  const [authReady, setAuthReady] = useState(false)
  const [email, setEmail] = useState('')
  const [notice, setNotice] = useState<UiNotice>()
  const [sending, setSending] = useState(false)
  const [installAvailable, setInstallAvailable] = useState(appInstaller.available())
  const [installGuide, setInstallGuide] = useState(false)
  const [updateReady, setUpdateReady] = useState(appUpdater.ready())
  const [propertyId, setPropertyId] = useState<string>()
  const [boundary, setBoundary] = useState<PropertyBoundary>()
  const [fieldSession, setFieldSession] = useState<FieldSession>()
  const [field, setField] = useState<FieldSnapshot>(emptyField)
  const [fieldStatus, setFieldStatus] = useState<string>()
  const [trailName, setTrailName] = useState('')
  const [trailDescription, setTrailDescription] = useState('')
  const [confirmTrailDiscard, setConfirmTrailDiscard] = useState(false)
  const [sheetDetent, setSheetDetent] = useState<'peek' | 'half' | 'full'>('peek')
  const [gpsPrimer, setGpsPrimer] = useState(false)
  const [activeView, setActiveView] = useState<ActiveView>('map')
  const [pinStore, setPinStore] = useState<PinStore>()
  const [pins, setPins] = useState<StoredPin[]>([])
  const [selectedPin, setSelectedPin] = useState<StoredPin>()
  const [editingPin, setEditingPin] = useState(false)
  const [confirmPinDelete, setConfirmPinDelete] = useState(false)
  const [choosingPin, setChoosingPin] = useState(false)
  const [pinLocation, setPinLocation] = useState<{ latitude: number; longitude: number; source: 'position' | 'map' }>()
  const [pinCategory, setPinCategory] = useState<PinCategory>('general')
  const [pinTitle, setPinTitle] = useState('')
  const [pinDescription, setPinDescription] = useState('')
  const [presenceClient, setPresenceClient] = useState<PresenceClient>()
  const [presence, setPresence] = useState<PresenceSnapshot>(emptyPresence)
  const [selectedPerson, setSelectedPerson] = useState<PresenceFix>()
  const [presenceNow, setPresenceNow] = useState(Date.now())
  const [mediaStore, setMediaStore] = useState<MediaStore>()
  const [mediaItems, setMediaItems] = useState<StoredMedia[]>([])
  const [fieldClient, setFieldClient] = useState<FieldClient>()
  const [mediaTab, setMediaTab] = useState<'capture' | 'library'>('capture')
  const [historySegments, setHistorySegments] = useState<HistorySegment[]>([])
  const [selectedHistoryUserId, setSelectedHistoryUserId] = useState<string>()
  const [captureBlocked, setCaptureBlocked] = useState(false)
  const [trailMaxAccuracy, setTrailMaxAccuracy] = useState(15)
  const [layers, setLayers] = useState<MapLayers>({ boundary: true, trails: true, pins: true, people: true })
  const [selectedMapLocation, setSelectedMapLocation] = useState<{ latitude: number; longitude: number }>()

  const memberships: PropertyMembership[] = user?.properties ?? []
  const membership = memberships.find((entry) => entry.id === propertyId)
  const isPropertyAdmin = membership?.role === 'admin'
  const railSections = useMemo<RailSection[]>(() => isPropertyAdmin ? [...memberRail, { label: 'Admin', items: [
    { id: 'admin-people', label: 'People', icon: Users },
    { id: 'storage', label: 'Storage', icon: HardDrive }
  ] }] : memberRail, [isPropertyAdmin])

  useEffect(() => platform.network.subscribe(setOnline), [])
  useEffect(() => {
    const authResult = new URLSearchParams(location.search).get('auth')
    if (authResult === 'expired') {
      setNotice({ tone: 'fail', title: 'Link unavailable', message: 'That sign-in link is invalid, expired, or has already been used. Request a new link below.' })
      history.replaceState({}, '', location.pathname)
    }
    void authClient.currentUser().then(setUser).catch(() => undefined).finally(() => setAuthReady(true))
  }, [])
  useEffect(() => appInstaller.subscribe(setInstallAvailable), [])
  useEffect(() => {
    if (appUpdater.consumeApplied()) setNotice({ tone: 'ok', title: 'App updated', message: 'The newest version is ready to use.' })
    const unsubscribe = appUpdater.subscribe(setUpdateReady)
    setUpdateReady(appUpdater.ready())
    return unsubscribe
  }, [])

  // Choose a property as soon as we know which ones the user belongs to.
  useEffect(() => {
    if (!user) { setPropertyId(undefined); return }
    setPropertyId(propertyStore.select(user.properties)?.id)
  }, [user])

  // Everything below the property is rebuilt whenever the property changes.
  useEffect(() => {
    if (!user || !propertyId) { setFieldSession(undefined); setBoundary(undefined); setField(emptyField); setPinStore(undefined); setPins([]); setPresenceClient(undefined); setPresence(emptyPresence); setMediaStore(undefined); setMediaItems([]); setFieldClient(undefined); setCaptureBlocked(false); return }

    let disposed = false
    let session: FieldSession | undefined
    let unsubscribe: (() => void) | undefined
    let unsubscribePresence: (() => void) | undefined
    let livePresence: PresenceClient | undefined

    setSelectedPin(undefined); setSelectedPerson(undefined); setPinLocation(undefined); setChoosingPin(false); setActiveView('map')
    propertyStore.remember(propertyId)
    // Render immediately from the cached boundary when there is one, so a cold
    // offline start still shows the map.
    setBoundary(propertyStore.cachedBoundary(propertyId))
    precacheTiles(propertyId)

    const client = new FieldClient(propertyId)
    setFieldClient(client)
    void client.capacity().then((reading) => { if (!disposed) setCaptureBlocked(!reading.acceptingUploads) }).catch(() => undefined)
    const propertyMedia = new MediaStore(propertyId, user.id, db.media, client)
    setMediaStore(propertyMedia)
    void propertyMedia.list().then((loaded) => { if (!disposed) setMediaItems(loaded) })
    const propertyPins = new PinStore(propertyId, user.id, db.pins, client)
    setPinStore(propertyPins)
    void propertyPins.load(false).then((loaded) => { if (!disposed) setPins(loaded) })
    livePresence = new PresenceClient(propertyId, client)
    setPresenceClient(livePresence)
    unsubscribePresence = livePresence.subscribe((snapshot) => { setPresence(snapshot); setPresenceNow(Date.now()) })
    void livePresence.start()
    void Promise.all([client.settings(), client.boundary()])
      .then(([settings, geometry]) => {
        if (disposed) return
        const propertyBoundary = geometry as PropertyBoundary
        setTrailMaxAccuracy(settings.trailMaxAccuracyMeters)
        propertyStore.cacheBoundary(propertyId, propertyBoundary)
        setBoundary(propertyBoundary)
        session = new FieldSession(propertyId, platform.geolocation, propertyBoundary, settings, db.positions, db.trailDrafts, client)
        unsubscribe = session.subscribe(setField)
        setFieldSession(session)
        return session.loadTrails().then(() => membership?.role === 'admin' ? session?.recoverTrailDraft(user.id) : undefined)
      })
      .catch(() => setFieldStatus('Field tools could not load. Check your connection and try again.'))

    return () => { disposed = true; unsubscribe?.(); unsubscribePresence?.(); session?.stopTracking(); livePresence?.stop() }
  }, [user?.id, propertyId])

  useEffect(() => {
    if (!pinStore || !online) return
    void pinStore.load(true).then((loaded) => {
      setPins(loaded)
      setSelectedPin((current) => current ? loaded.find((pin) => pin.id === current.id) : undefined)
    })
  }, [pinStore, online])

  useEffect(() => {
    if (!mediaStore || !online) return
    void mediaStore.sync().then(() => mediaStore.list()).then(setMediaItems)
  }, [mediaStore, online])
  useEffect(() => {
    if (!mediaStore) return
    return mediaStore.subscribe(() => { void mediaStore.list().then(setMediaItems) })
  }, [mediaStore])

  useEffect(() => {
    if (field.fix && field.fence === 'inside') presenceClient?.send(field.fix)
  }, [presenceClient, field.fix?.recordedAt, field.fence])

  useEffect(() => {
    if (presence.people.length === 0 || presence.connection !== 'live') return
    const timer = setInterval(() => setPresenceNow(Date.now()), 30_000)
    return () => clearInterval(timer)
  }, [presence.people.length, presence.connection])

  useEffect(() => {
    setSelectedPerson((current) => current ? presence.people.find((person) => person.userId === current.userId) : undefined)
  }, [presence.people])

  useEffect(() => {
    if (field.trailMode === 'review' && field.trailDraft) {
      setTrailName(field.trailDraft.name); setTrailDescription(field.trailDraft.description)
    }
  }, [field.trailMode, field.trailDraft?.id])

  async function requestLink(event: FormEvent) {
    event.preventDefault()
    if (!email.trim()) return
    setSending(true); setNotice(undefined)
    try {
      await authClient.requestLink(email)
      setNotice({ tone: 'ok', title: 'Check your email', message: 'If that address has access, its sign-in link will arrive shortly and expire in 15 minutes.' })
    } catch { setNotice({ tone: 'fail', title: 'Could not send', message: 'The sign-in link could not be sent. Check your connection and try again.' }) }
    finally { setSending(false) }
  }

  async function logout() {
    fieldSession?.stopTracking()
    await authClient.logout()
    let mapCleared = true
    try { await clearPrivateCaches() } catch { mapCleared = false }
    setUser(undefined)
    setNotice(mapCleared
      ? { tone: 'info', title: 'Signed out', message: 'Your server session and offline property map have been cleared. Sign in again to open property information.' }
      : { tone: 'fail', title: 'Signed out, but cleanup failed', message: 'Your server session has ended, but this browser could not remove its offline property map. Clear this site\'s stored data before giving the device to someone else.' })
  }

  async function installApp() {
    const result = await appInstaller.install()
    if (result === 'accepted') setNotice({ tone: 'ok', title: 'Installing app', message: 'Country House Trails is being added to this device.' })
    if (result === 'dismissed') setNotice({ tone: 'info', title: 'Installation cancelled', message: 'Nothing changed. You can install the app later from this screen.' })
    if (result === 'unavailable' && appInstaller.appleManualInstallAvailable()) setInstallGuide(true)
  }

  function toggleGps() {
    if (!fieldSession || !user) return
    setFieldStatus(undefined)
    if (field.tracking) fieldSession.stopTracking()
    else { setGpsPrimer(true); setSheetDetent('half') }
  }

  async function applyUpdate() {
    if (!online) return
    await appUpdater.apply()
  }

  function beginGps() {
    if (!fieldSession || !user) return
    setGpsPrimer(false)
    setFieldStatus(undefined)
    fieldSession.startTracking(user.id)
  }

  function advanceSheet() {
    setSheetDetent((current) => current === 'peek' ? 'half' : current === 'half' ? 'full' : 'peek')
  }

  function showView(view: ActiveView) {
    setActiveView(view)
    setChoosingPin(false)
    setPinLocation(undefined)
    setSelectedPerson(undefined)
    setSelectedMapLocation(undefined)
    if (view !== 'map') setSheetDetent('half')
  }

  async function refreshMedia() {
    if (mediaStore) setMediaItems(await mediaStore.list())
    if (fieldClient && online) void fieldClient.capacity().then((reading) => setCaptureBlocked(!reading.acceptingUploads)).catch(() => undefined)
  }
  async function refreshQueue() {
    if (pinStore) setPins(await pinStore.load(online))
    if (mediaStore) { if (online) await mediaStore.sync(); setMediaItems(await mediaStore.list()) }
  }
  async function refreshTrails() { await fieldSession?.loadTrails() }

  function choosePin(latitude: number, longitude: number, source: 'position' | 'map' = 'map') {
    if (!boundary || !inBoundary({ latitude, longitude }, boundary)) {
      setNotice({ tone: 'fail', title: 'Outside the property', message: 'Pins must be placed inside the property boundary.' })
      return
    }
    setActiveView('pins'); setChoosingPin(false); setSelectedPin(undefined)
    setPinLocation({ latitude, longitude, source }); setPinCategory('general'); setPinTitle(''); setPinDescription('')
    setSheetDetent('full')
  }

  async function savePin(event: FormEvent) {
    event.preventDefault()
    if (!pinStore || !pinLocation || !pinTitle.trim()) return
    try {
      const pin = await pinStore.create({
        latitude: pinLocation.latitude, longitude: pinLocation.longitude,
        category: pinCategory, title: pinTitle.trim(), description: pinDescription.trim()
      }, online)
      setPins(await pinStore.list()); setPinLocation(undefined); setSelectedPin(pin)
      setNotice(pin.syncState === 'synced'
        ? { tone: 'ok', title: 'Pin saved', message: 'The pin is on the property map.' }
        : { tone: 'info', title: 'Saved on this device', message: 'The pin will upload automatically when a connection is available.' })
    } catch {
      setNotice({ tone: 'fail', title: 'Pin not saved', message: 'This device could not store the pin. Free device storage and try again.' })
    }
  }

  function beginPinEdit(pin: StoredPin) {
    setPinLocation({ latitude: pin.latitude, longitude: pin.longitude, source: 'map' })
    setPinCategory(pin.category); setPinTitle(pin.title); setPinDescription(pin.description)
    setEditingPin(true); setSheetDetent('full')
  }

  async function savePinEdit(event: FormEvent) {
    event.preventDefault()
    if (!pinStore || !selectedPin || !pinLocation || !pinTitle.trim()) return
    try {
      const updated = await pinStore.update(selectedPin, {
        latitude: pinLocation.latitude, longitude: pinLocation.longitude,
        category: pinCategory, title: pinTitle.trim(), description: pinDescription.trim()
      }, online)
      setPins(await pinStore.list()); setSelectedPin(updated); setPinLocation(undefined); setEditingPin(false)
      setNotice({ tone: 'ok', title: 'Pin updated', message: updated.syncState === 'synced' ? 'The change is on the property map.' : 'The change is safe on this device and will upload when online.' })
    } catch (error) { setNotice({ tone: 'fail', title: 'Pin not updated', message: error instanceof Error ? error.message : 'The pin could not be updated.' }) }
  }

  async function deleteSelectedPin() {
    if (!pinStore || !selectedPin) return
    try {
      await pinStore.delete(selectedPin, online); setPins(await pinStore.list()); setSelectedPin(undefined); setConfirmPinDelete(false)
      setNotice({ tone: 'ok', title: 'Pin deleted', message: 'The pin was removed from the property map.' })
    } catch (error) { setConfirmPinDelete(false); setNotice({ tone: 'fail', title: 'Pin not deleted', message: error instanceof Error ? error.message : 'The pin could not be deleted.' }) }
  }

  function startTrail() {
    if (!fieldSession || !user) return
    setTrailName(''); setTrailDescription(''); setFieldStatus(undefined)
    try { fieldSession.startTrail(user.id) } catch (error) { setFieldStatus(error instanceof Error ? error.message : 'Trail recording could not start.') }
  }

  async function publishTrail(event: FormEvent) {
    event.preventDefault()
    if (!fieldSession) return
    setFieldStatus(undefined)
    try {
      await fieldSession.publishTrail(trailName, trailDescription)
      setFieldStatus('Trail published.')
      setTrailName(''); setTrailDescription('')
    } catch (error) { setFieldStatus(error instanceof Error ? error.message : 'Trail could not be published.') }
  }

  async function saveTrailDraft() {
    if (!fieldSession) return
    setFieldStatus(undefined)
    try {
      await fieldSession.saveTrailDraft(trailName, trailDescription)
      setFieldStatus('Draft saved to the server. It remains editable until you publish it.')
    } catch {
      setFieldStatus('Draft is safe on this device. It could not sync yet and will need another save when online.')
    }
  }

  async function discardTrail() {
    await fieldSession?.discardTrail(); setConfirmTrailDiscard(false); setTrailName(''); setTrailDescription('')
  }

  const accuracy = field.fix ? `${Math.round(field.fix.accuracyMeters)} m` : '--'
  const queuedItems = pins.filter((pin) => pin.syncState !== 'synced').length + mediaItems.filter((item) => item.syncState !== 'synced').length
  const fenceLabel = field.fence === 'inside' ? 'ON PROPERTY' : field.fence === 'outside' ? 'OFF PROPERTY' : 'CHECKING FENCE'
  const lastSync = field.lastSyncAt ? new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }).format(field.lastSyncAt) : undefined
  const eyebrow = useMemo(() => {
    if (!user) return 'PRIVATE LAND MAP'
    return membership ? `${membership.role.toUpperCase()} / ${membership.name.toUpperCase()}` : 'NO PROPERTY'
  }, [user, membership])

  return <main className={`app-shell ${user ? 'has-context-rail' : ''}`}>
    {user && <ContextRail sections={railSections} activeId={activeView} onSelect={(id) => { if (['map', 'layers', 'people', 'pins', 'media', 'trails', 'history', 'queue', 'admin-people', 'storage'].includes(id)) showView(id as ActiveView) }} logoSrc={brandMark} />}
    {propertyId && boundary
      ? <MapView propertyId={propertyId} boundary={boundary} ownFix={field.fix} trailDraft={field.trailDraft} trailReview={field.trailMode === 'review'} trails={field.trails} pins={pins} presence={presence.people.filter((person) => person.userId !== user?.id)} history={activeView === 'history' ? historySegments : []} layers={layers} {...(selectedMapLocation ? { selectedLocation: selectedMapLocation } : {})} selectedHistoryUserId={selectedHistoryUserId} selectedPinId={selectedPin?.id} selectedPersonId={selectedPerson?.userId} choosePinOnClick={choosingPin} onChoosePin={activeView === 'pins' ? (latitude, longitude) => choosePin(latitude, longitude) : undefined} onSelectPin={(pin) => { setActiveView('pins'); setSelectedPin(pin as StoredPin); setSelectedPerson(undefined); setSelectedMapLocation(undefined); setPinLocation(undefined); setSheetDetent('half') }} onSelectPerson={(person) => { setActiveView('people'); setSelectedPerson(person); setSelectedPin(undefined); setSelectedMapLocation(undefined); setSheetDetent('half') }} />
      : <div className="map" aria-hidden="true" />}

    <StatusStrip online={online} tracking={field.tracking} queued={queuedItems} fence={field.fence} {...(user && membership ? { onQueueClick: () => showView('queue') } : {})} {...(field.fix ? { accuracyMeters: field.fix.accuracyMeters } : {})} {...(lastSync ? { lastSync } : {})} />

    <section className={`context-surface ${user ? 'is-authenticated' : 'is-auth'} detent-${user ? sheetDetent : 'half'}`} role="dialog" aria-label="Country House Trails">
      {user
        ? <button type="button" className="sheet-handle" aria-label={`Change information panel from ${sheetDetent}`} onClick={advanceSheet}><span /></button>
        : <div className="sheet-handle sheet-handle--static" aria-hidden="true"><span /></div>}
      <p className="eyebrow">{eyebrow}</p>
      <h1>{user && membership ? 'On the land' : 'Country House Trails'}</h1>

      {!authReady ? <p className="intro">Checking your sign-in...</p> : user ? <>
        <p className="intro">Signed in as <strong>{user.displayName}</strong><br /><span className="data">{user.email}</span></p>

        {memberships.length === 0 && <p className="field-message">
          Your account is not a member of any property yet. Ask an administrator to invite you.
        </p>}

        {/* A selector only earns its space once there is a choice to make. */}
        {memberships.length > 1 && <div className="property-picker">
          <label htmlFor="property">Property</label>
          <select id="property" value={propertyId ?? ''} onChange={(event) => setPropertyId(event.target.value)}>
            {memberships.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
          </select>
        </div>}

        {membership && <>
        <div className="view-switcher" aria-label="Map tools">
          <Button type="button" variant={activeView === 'map' ? 'primary' : 'secondary'} onClick={() => showView('map')}>Field</Button>
          <Button type="button" variant={activeView === 'people' ? 'primary' : 'secondary'} onClick={() => showView('people')}>People</Button>
          <Button type="button" variant={activeView === 'pins' ? 'primary' : 'secondary'} onClick={() => showView('pins')}>Pins {pins.length ? `(${pins.length})` : ''}</Button>
          <Button type="button" variant={activeView === 'media' ? 'primary' : 'secondary'} onClick={() => showView('media')}>Capture</Button>
        </div>
        <div className="review-switcher" aria-label="Review tools"><Button type="button" variant={activeView === 'layers' ? 'primary' : 'secondary'} onClick={() => showView('layers')}>Layers</Button><Button type="button" variant={activeView === 'queue' ? 'primary' : 'secondary'} onClick={() => showView('queue')}>Queue {queuedItems ? `(${queuedItems})` : ''}</Button><Button type="button" variant={activeView === 'trails' ? 'primary' : 'secondary'} onClick={() => showView('trails')}>Trails</Button><Button type="button" variant={activeView === 'history' ? 'primary' : 'secondary'} onClick={() => showView('history')}>History</Button>{isPropertyAdmin && <><Button type="button" variant={activeView === 'admin-people' ? 'primary' : 'secondary'} onClick={() => showView('admin-people')}>Admin people</Button><Button type="button" variant={activeView === 'storage' ? 'primary' : 'secondary'} onClick={() => showView('storage')}>Storage</Button></>}</div>

        {activeView === 'map' ? <div className="field-controls">
          {gpsPrimer && !field.tracking ? <div className="gps-primer">
            <InlineNotice tone="info" title="Foreground location only">Country House Trails uses your location only while this app is open and GPS is on. It does not track you in the background.</InlineNotice>
            <div className="button-row"><Button type="button" variant="secondary" onClick={() => setGpsPrimer(false)}>Keep browsing</Button><Button type="button" icon={Navigation} onClick={beginGps}>Continue</Button></div>
          </div> : !(isPropertyAdmin && field.trailMode === 'idle') && <Button type="button" size="lg" icon={Navigation} disabled={!fieldSession} onClick={toggleGps}>{field.tracking ? 'Turn GPS off' : 'Turn GPS on'}</Button>}
          {field.tracking && <div className="field-readout" aria-live="polite"><span>{fenceLabel}</span><span>ACCURACY {accuracy}</span></div>}
          {field.fix && field.fix.accuracyMeters > trailMaxAccuracy && <InlineNotice tone="pending" title="Poor GPS accuracy">Your position is ±{Math.round(field.fix.accuracyMeters)} m. Wait for a clearer fix or use the recenter control to find your white position marker.</InlineNotice>}
          {isPropertyAdmin && field.trailMode === 'idle' && <div className="trail-start-gate">
            <div className={!field.tracking || !field.fix ? 'required' : ''}><span>GPS</span><strong>{!field.tracking ? 'OFF · REQUIRED' : field.fix ? `READY · ±${Math.round(field.fix.accuracyMeters)} m` : 'REQUESTING FIX · REQUIRED'}</strong></div>
            <div className={field.fix && field.fence !== 'inside' ? 'required' : ''}><span>Property fence</span><strong>{!field.fix ? 'WAITING FOR GPS' : field.fence === 'inside' ? 'INSIDE · READY' : field.fence === 'outside' ? 'OUTSIDE · REQUIRED' : 'CHECKING · REQUIRED'}</strong></div>
            {!field.tracking
              ? <Button type="button" size="lg" icon={Navigation} onClick={toggleGps}>Turn GPS on to record</Button>
              : <Button type="button" size="lg" disabled={!field.fix || field.fence !== 'inside'} onClick={startTrail}>{!field.fix ? 'Waiting for GPS fix' : field.fence === 'inside' ? 'Start recording' : 'Walk onto the property'}</Button>}
          </div>}
          {field.trailMode === 'recording' && <>
            <div className="recording-indicator"><span /> RECORDING TRAIL</div>
            <div className="trail-metrics"><span>{Math.round(field.trailDraft?.distanceMeters ?? 0)} m</span><span>{field.trailDraft?.kept ?? 0} kept</span><span>{field.trailDraft?.discarded ?? 0} discarded</span></div>
            {field.trailIssue === 'poor-accuracy' && <InlineNotice tone="pending" title="Poor GPS accuracy">That fix was discarded and is not part of the trail. Keep walking when accuracy improves.</InlineNotice>}
            <div className="button-row"><Button type="button" size="lg" variant="secondary" onClick={() => fieldSession?.pauseTrail()}>Pause</Button><Button type="button" size="lg" onClick={() => fieldSession?.finishTrail()}>Stop & review</Button></div>
          </>}
          {field.trailMode === 'paused' && <>
            <div className="recording-indicator paused">PAUSED</div>
            {field.trailIssue === 'outside-property' && <InlineNotice tone="pending" title="Paused at the fence">Recording paused automatically when the confirmed position moved outside. Return inside before resuming.</InlineNotice>}
            <div className="button-row"><Button type="button" size="lg" disabled={field.fence !== 'inside'} onClick={() => fieldSession?.resumeTrail()}>Resume</Button><Button type="button" size="lg" variant="secondary" onClick={() => fieldSession?.finishTrail()}>Review trail</Button></div>
          </>}
          {field.trailMode === 'review' && <form className="trail-review" onSubmit={(event) => void publishTrail(event)}>
            <div className="trail-metrics"><span>{Math.round(field.trailDraft?.distanceMeters ?? 0)} m</span><span>{field.trailDraft?.kept ?? 0} points</span></div>
            <p className="data">DRAFT · {field.trailDraft?.syncState === 'server' ? 'SERVER COPY SAVED' : field.trailDraft?.syncState === 'syncing' ? 'SYNCING' : 'ON THIS DEVICE'}</p>
            <div className="button-row"><Button type="button" variant="secondary" onClick={() => void fieldSession?.trimTrail()}>Trim last point</Button><Button type="button" variant="ghost" disabled={!field.trailDraft?.trimmedTail.length} onClick={() => void fieldSession?.undoTrailTrim()}>Undo trim</Button></div>
            <p className="data">TRIMMING IS REVERSIBLE UNTIL YOU PUBLISH</p>
            <label htmlFor="trail-name">Trail name</label><input id="trail-name" required value={trailName} onChange={(event) => setTrailName(event.target.value)} />
            <label htmlFor="trail-description">Description</label><textarea id="trail-description" value={trailDescription} onChange={(event) => setTrailDescription(event.target.value)} />
            <div className="button-row"><Button type="button" variant="secondary" onClick={() => void saveTrailDraft()}>Save draft</Button><Button type="submit">Publish trail</Button></div>
            <Button type="button" variant="ghost" onClick={() => setConfirmTrailDiscard(true)}>Discard draft</Button>
            {confirmTrailDiscard && <ConfirmSheet title="Discard this trail?" onCancel={() => setConfirmTrailDiscard(false)} actions={<><Button type="button" variant="secondary" onClick={() => setConfirmTrailDiscard(false)}>Keep the draft</Button><Button type="button" variant="danger" onClick={() => void discardTrail()}>Discard</Button></>}>The recorded {Math.round(field.trailDraft?.distanceMeters ?? 0)} m draft will be permanently removed from this device.</ConfirmSheet>}
          </form>}
          {field.error && <InlineNotice tone="fail" title="Location is blocked" action={<Button type="button" variant="secondary" onClick={() => setGpsPrimer(true)}>Review location steps</Button>}>Enable location for this site in your browser settings, then try again. You can keep browsing the map without GPS.</InlineNotice>}
          {fieldStatus && <p className="field-message" aria-live="polite">{fieldStatus}</p>}
        </div> : activeView === 'layers' ? <div className="layer-controls"><h2>Map layers</h2>{([['boundary', 'Property boundary'], ['people', 'People'], ['pins', 'Pins'], ['trails', 'Trails']] as const).map(([id, label]) => <label key={id}><span><span className={`legend-mark ${id}`} />{label}</span><input type="checkbox" checked={layers[id]} onChange={(event) => setLayers((current) => ({ ...current, [id]: event.target.checked }))} /></label>)}<p className="data">YOUR OWN POSITION IS ALWAYS WHITE AND CANNOT BE HIDDEN</p></div> : activeView === 'people' ? <div className="presence-controls">
          {selectedPerson ? <div className="pin-detail">
            <p className="data">{stalenessBucket(Math.max(0, presenceNow - selectedPerson.recordedAt)).replace('-', ' ').toUpperCase()}</p>
            <h2>{selectedPerson.displayName}</h2>
            <p>Last location update {formatPresenceAge(Math.max(0, presenceNow - selectedPerson.recordedAt))}.</p>
            <p>{boundary && inBoundary(selectedPerson, boundary) ? 'On the property' : 'Outside the property'}{field.fix ? ` · ${formatMapDistance(field.fix, selectedPerson)} from you` : ' · distance unavailable without your GPS'}</p>
            <p className="data">ACCURACY ±{Math.round(selectedPerson.accuracyMeters)} M</p>
            <Button type="button" variant="secondary" onClick={() => setSelectedPerson(undefined)}>Back to people</Button>
          </div> : <>
            {presence.connection !== 'live' && <InlineNotice tone="pending" title="Reconnecting">Showing the latest known positions while live updates reconnect. Their displayed ages are frozen until the connection returns.</InlineNotice>}
            {presence.snapshotStatus === 'unavailable' && <InlineNotice tone="fail" title="Last known positions unavailable">Live updates may still arrive, but the saved presence snapshot could not be loaded.</InlineNotice>}
            {presence.people.filter((person) => person.userId !== user.id).length === 0
              ? <p className="field-message">{presence.snapshotStatus === 'ready' ? 'No one else has shared an on-property position yet.' : 'Waiting for the latest known positions.'}</p>
              : <div className="pin-list">{presence.people.filter((person) => person.userId !== user.id).map((person) => <button type="button" className="pin-row" key={person.userId} onClick={() => setSelectedPerson(person)}>
                <span><strong>{person.displayName}</strong><small>{stalenessBucket(Math.max(0, presenceNow - person.recordedAt)).replace('-', ' ')}</small></span>
                <small>{formatPresenceAge(Math.max(0, presenceNow - person.recordedAt))}</small>
              </button>)}</div>}
          </>}
        </div> : activeView === 'pins' ? <div className="pin-controls">
          {pinLocation ? <form className="pin-form" onSubmit={(event) => void (editingPin ? savePinEdit(event) : savePin(event))}>
            <p className="data pin-source">SOURCE · {pinLocation.source === 'position' ? 'CURRENT POSITION' : 'CHOSEN MAP POINT'}</p>
            <label htmlFor="pin-category">Category</label>
            <select id="pin-category" value={pinCategory} onChange={(event) => setPinCategory(event.target.value as PinCategory)}>
              {PIN_CATEGORIES.map((category) => <option key={category.value} value={category.value}>{category.label}</option>)}
            </select>
            <label htmlFor="pin-title">Title</label>
            <input id="pin-title" required maxLength={120} value={pinTitle} onChange={(event) => setPinTitle(event.target.value)} />
            <label htmlFor="pin-description">Notes</label>
            <textarea id="pin-description" maxLength={2000} value={pinDescription} onChange={(event) => setPinDescription(event.target.value)} />
            {!online && <InlineNotice tone="pending" title="Offline">This pin saves to this phone now and uploads when you reconnect.</InlineNotice>}
            <div className="button-row"><Button type="button" variant="secondary" onClick={() => { setPinLocation(undefined); setEditingPin(false) }}>Cancel</Button><Button type="submit">{editingPin ? 'Save changes' : online ? 'Save pin' : 'Save on this phone'}</Button></div>
          </form> : selectedPin ? <div className="pin-detail">
            <p className="data">{(PIN_CATEGORIES.find((category) => category.value === selectedPin.category)?.label ?? 'General note').toUpperCase()}</p>
            <h2>{selectedPin.title}</h2>
            {selectedPin.description && <p>{selectedPin.description}</p>}
            <p className="data">{selectedPin.latitude.toFixed(5)}, {selectedPin.longitude.toFixed(5)}</p>
            <p className="data">CREATED BY {selectedPin.authorId === user.id ? 'YOU' : (selectedPin.authorDisplayName ?? 'PROPERTY MEMBER').toUpperCase()} · {new Date(selectedPin.createdAt).toLocaleDateString()}</p>
            {selectedPin.syncState !== 'synced' && <InlineNotice tone="pending" title="Waiting to upload">This pin is safe on this device and will retry when online.</InlineNotice>}
            {(selectedPin.authorId === user.id || isPropertyAdmin) && <div className="button-row"><Button type="button" variant="secondary" disabled={selectedPin.syncState === 'synced' && !online} onClick={() => beginPinEdit(selectedPin)}>Edit</Button><Button type="button" variant="ghost" disabled={selectedPin.syncState === 'synced' && !online} onClick={() => setConfirmPinDelete(true)}>Delete</Button></div>}
            {confirmPinDelete && <ConfirmSheet title={`Delete ${selectedPin.title}?`} onCancel={() => setConfirmPinDelete(false)} actions={<><Button type="button" variant="secondary" onClick={() => setConfirmPinDelete(false)}>Keep pin</Button><Button type="button" variant="danger" onClick={() => void deleteSelectedPin()}>Delete</Button></>}>This removes the pin from the property map and cannot be undone.</ConfirmSheet>}
            <Button type="button" variant="secondary" onClick={() => setSelectedPin(undefined)}>Back to pins</Button>
          </div> : <>
            {choosingPin && <InlineNotice tone="info" title="Choose a point">Tap the map where the pin belongs. You can also press and hold a point on the map at any time from Pins.</InlineNotice>}
            <div className="button-row">
              <Button type="button" size="lg" icon={MapPin} disabled={!field.fix || field.fence !== 'inside'} onClick={() => { if (field.fix) choosePin(field.fix.latitude, field.fix.longitude, 'position') }}>Pin where I am</Button>
              <Button type="button" size="lg" variant="secondary" disabled={!boundary} onClick={() => { setChoosingPin(true); setSheetDetent('peek') }}>Choose on map</Button>
            </div>
            {pins.length === 0 ? <p className="field-message">No pins yet. Add the first landmark, hazard, or field note.</p> : <div className="pin-list">
              {pins.map((pin) => <button type="button" className="pin-row" key={pin.id} onClick={() => { setSelectedPin(pin); setSheetDetent('half') }}>
                <span><strong>{pin.title}</strong><small>{PIN_CATEGORIES.find((category) => category.value === pin.category)?.label ?? 'General note'}</small></span>
                <small>{pin.syncState === 'synced' ? 'ON MAP' : 'QUEUED'}</small>
              </button>)}
            </div>}
          </>}
        </div> : activeView === 'media' && mediaStore && fieldClient ? <div className="media-workspace"><div className="button-row"><Button type="button" variant={mediaTab === 'capture' ? 'primary' : 'secondary'} onClick={() => setMediaTab('capture')}>Capture</Button><Button type="button" variant={mediaTab === 'library' ? 'primary' : 'secondary'} onClick={() => setMediaTab('library')}>Library</Button></div>{mediaTab === 'capture'
          ? <MediaCapture store={mediaStore} items={mediaItems} online={online} {...(field.fix ? { fix: field.fix } : {})} fence={field.fence} captureBlocked={captureBlocked} onPlacePin={() => showView('pins')} onItemsChanged={refreshMedia} />
          : <MediaLibrary client={fieldClient} localStore={mediaStore} userId={user.id} admin={Boolean(isPropertyAdmin)} onSelectLocation={setSelectedMapLocation} onChanged={refreshMedia} />}</div> : activeView === 'history' && fieldClient ? <HistoryPanel client={fieldClient} admin={Boolean(isPropertyAdmin)} {...(selectedHistoryUserId ? { requestedUserId: selectedHistoryUserId } : {})} onSegments={setHistorySegments} onSelectUser={setSelectedHistoryUserId} /> : activeView === 'trails' && fieldClient ? <TrailManager client={fieldClient} admin={Boolean(isPropertyAdmin)} onChanged={refreshTrails} onSelectLocation={setSelectedMapLocation} /> : activeView === 'queue' && pinStore && mediaStore ? <QueuePanel online={online} pins={pins} media={mediaItems} pinStore={pinStore} mediaStore={mediaStore} onChanged={refreshQueue} /> : activeView === 'admin-people' && fieldClient && isPropertyAdmin ? <PeopleAdmin client={fieldClient} currentUserId={user.id} online={online} onHistory={(personId) => { setSelectedHistoryUserId(personId); showView('history') }} /> : activeView === 'storage' && fieldClient && mediaStore && isPropertyAdmin ? <StoragePanel client={fieldClient} mediaStore={mediaStore} uploads={mediaItems} onChanged={refreshMedia} /> : <p className="field-message">This review tool is still loading.</p>}
        </>}

        <Button type="button" variant="secondary" className="sign-out" onClick={() => void logout()}>Sign out</Button>
      </> : <>
        <p className="intro">Sign in with your invited email address to see family presence, pins, media, and trail history.</p>
        <form className="auth-form" onSubmit={(event) => void requestLink(event)}>
          <label htmlFor="email">Email address</label>
          <input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
          <Button type="submit" loading={sending}>Email me a sign-in link</Button>
        </form>
      </>}

      {notice && <InlineNotice tone={notice.tone} title={notice.title}>{notice.message}</InlineNotice>}
      {updateReady && <InlineNotice tone={online ? 'ok' : 'pending'} title={online ? 'Update ready' : 'Update waiting'} action={online ? <Button type="button" onClick={() => void applyUpdate()}>Update now</Button> : undefined}>{online ? 'Install the latest version now. The app will reopen after the update.' : 'The update will remain ready. Reconnect before installing it; queued field work is unchanged.'}</InlineNotice>}
      {installGuide && <InlineNotice tone="info" title="Install on iPhone or iPad" action={<Button type="button" variant="secondary" onClick={() => setInstallGuide(false)}>Done</Button>}><ol className="install-steps"><li>Open this page in Safari.</li><li>Tap the Share button.</li><li>Choose Add to Home Screen, then Add.</li></ol></InlineNotice>}
      {(installAvailable || appInstaller.appleManualInstallAvailable()) && <Button type="button" variant="secondary" className="install-button" onClick={() => void installApp()}>{installAvailable ? 'Install app' : 'How to install'}</Button>}
      <div className="legend"><span className="person" /> People <span className="pin" /> Pins & trails</div>
    </section>
  </main>
}

function formatMapDistance(from: { latitude: number; longitude: number }, to: { latitude: number; longitude: number }): string {
  const radians = Math.PI / 180
  const dLat = (to.latitude - from.latitude) * radians, dLon = (to.longitude - from.longitude) * radians
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(from.latitude * radians) * Math.cos(to.latitude * radians) * Math.sin(dLon / 2) ** 2
  const meters = 2 * 6_371_008.8 * Math.asin(Math.min(1, Math.sqrt(a)))
  return meters >= 1_000 ? `${(meters / 1_000).toFixed(1)} km` : `${Math.round(meters)} m`
}
